import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

// Globals
import { useDispatch, useSelector } from 'react-redux';
import Masonry from 'react-masonry-css';

// UI
import { Col, Input, Row, Tabs } from 'antd';
import CardFormTextArea from './CardFormTextArea';
import { SearchOutlined } from '@ant-design/icons';
import deleteIcon from '../../studio/assets/feedcardIcons/deleteIcon.png';
import loading from '../../studio/assets/loading2.gif';

// Locals
import { addCardProps, updateGraphicFormScrollPos } from '../../actions';
import { defaultImageEditorProps, handleInputChange } from '../utils';

// Types
import type { IState } from '../../graphql/types';

import styles from './CardForm.module.css';
import ImageEditor from '../ImageEditor/ImageEditor';
import { FeedCard } from '../../graphql/types';
import AvatarEditor from 'react-avatar-editor';
import useFeedCardForm from '../../hooks/useFeedCardForm';
import { ImageEditorProps } from '../../types';
import { API } from 'aws-amplify';
import { graphqlOperation } from '@aws-amplify/api';
import { giphySearch, unsplashSearch } from '../../graphql/queries';
import {
  FeedCardImageDetailsInput,
  FeedCardImageType,
  GiphySearchQuery,
  GiphySearchResult,
  UnsplashSearchQuery,
  UnsplashSearchResult,
} from '../../graphql/api';
import debounce from 'lodash.debounce';
import { useIsInViewport } from './Hooks/useIsInViewport';
import CardFormInput from './CardFormInput';
// import { nextTick } from 'process';
import { unsplashTrackDownload } from '../../graphql/mutations';
import giphyLogo from '../../studio/assets/giphy_logo.png';

const imageWidth = 360;
const maxSearchPage = 5;
let searchKey = Math.random(); // this is used to load wanted search e.g. quickly swap tabs etc.

const GraphicForm: React.FC<{ validationFailed: boolean; saving: boolean }> = ({
  validationFailed,
  saving,
}) => {
  const isUnsplashEnabled = useMemo(
    () => (process.env.REACT_APP_UNSPLASH_ENABLED || 'true') === 'true',
    []
  );
  const isGiphyEnabled = useMemo(
    () => (process.env.REACT_APP_GIPHY_ENABLED || 'true') === 'true',
    []
  );

  const dispatch = useDispatch();
  const [unsplashDownloadLink, setUnsplashDownloadLink] = useState<
    null | string
  >(null);
  const caption = useSelector<IState, string | undefined>(
    state =>
      state.cardProps?.imageDetails?.caption ||
      (state.cardProps?.caption as string | undefined)
  );
  const altDescription = useSelector<IState, string | undefined>(
    state => state.cardProps?.imageDetails?.altDescription as string | undefined
  );

  const cardProps = useSelector<IState, Partial<FeedCard> | undefined>(
    state => state.cardProps
  );

  const [editorRef, setEditorRef] = useState<AvatarEditor | null>(null);

  const { progress, handleRemove, handleImageSelect, updateImageCardProps } =
    useFeedCardForm(cardProps?.type);

  const [activeTab, setActiveTab] = useState<FeedCardImageType>(
    FeedCardImageType.userUploadedImage
  );

  const [searching, setSearching] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchImages, setSearchImages] = useState<
    (UnsplashSearchResult | GiphySearchResult)[]
  >([]);

  const [searchInput, setSearchInput] = useState('');
  const [searchType, setSearchType] = useState<FeedCardImageType>(
    FeedCardImageType.userUploadedImage
  );

  const loadMoreRef = useRef(null);
  const getNextPage = useIsInViewport(loadMoreRef);

  const isValidImageOnActiveTab = useMemo(() => {
    if (cardProps?.url || cardProps?.imageDetails?.url) {
      const isValidUploadedImage =
        activeTab === FeedCardImageType.userUploadedImage &&
        (typeof cardProps?.imageDetails === 'undefined' ||
          cardProps?.imageDetails?.imageTypeSelected ===
            FeedCardImageType.userUploadedImage);

      const isValidUnsplashImage =
        activeTab === FeedCardImageType.unsplashImage &&
        cardProps?.imageDetails?.imageTypeSelected ===
          FeedCardImageType.unsplashImage;

      const isValidGiphyImage =
        activeTab === FeedCardImageType.giphyImage &&
        cardProps?.imageDetails?.imageTypeSelected ===
          FeedCardImageType.giphyImage;

      return isValidGiphyImage || isValidUnsplashImage || isValidUploadedImage;
    } else {
      return false;
    }
  }, [cardProps?.url, cardProps?.imageDetails, activeTab]);

  const calcImageRatio = (currentWidth: number) => {
    return imageWidth / currentWidth;
  };

  useEffect(() => {
    dispatch(updateGraphicFormScrollPos(null));
  }, [activeTab, dispatch]);

  useEffect(() => {
    if (
      getNextPage &&
      !searching &&
      searchType !== FeedCardImageType.userUploadedImage &&
      !isValidImageOnActiveTab
    ) {
      setSearchPage(page => {
        if (page < maxSearchPage) {
          return page + 1;
        }
        return page;
      });
    }
  }, [
    getNextPage,
    searching,
    searchType,
    searchInput,
    dispatch,
    isValidImageOnActiveTab,
  ]);

  const searchDebounce = useMemo(
    () =>
      debounce((query: string, page: number) => {
        let currentSearchKey = Math.random();
        searchKey = currentSearchKey;

        if (searchType === FeedCardImageType.userUploadedImage) {
          return;
        }

        if (query.length === 0) {
          query = 'beautiful nature';
        }

        const types = {
          [FeedCardImageType.unsplashImage]: unsplashSearch,
          [FeedCardImageType.giphyImage]: giphySearch,
        };

        const getResult = (data: UnsplashSearchQuery | GiphySearchQuery) => {
          if ('unsplashSearch' in data) {
            return data.unsplashSearch;
          }
          if ('giphySearch' in data) {
            return data.giphySearch;
          }
          return null;
        };

        (
          API.graphql(
            graphqlOperation(types[searchType], {
              page,
              query,
            })
          ) as Promise<{ data: UnsplashSearchQuery | GiphySearchQuery }>
        ).then(data => {
          if (searchKey !== currentSearchKey) {
            return;
          }

          const result = getResult(data.data);
          if (result?.results) {
            setSearchImages(items => {
              const currentItems = page > 0 ? [...items] : [];
              for (const image of result.results || []) {
                if (image) {
                  currentItems.push(image);
                }
              }
              return currentItems;
            });
          } else {
            setSearchImages([]);
          }
          setTimeout(() => setSearching(false), 3000);
        });
      }, 1000),
    [searchType]
  );

  useEffect(() => {
    setSearching(true);
    searchDebounce(searchInput, searchPage);
  }, [searchInput, searchPage, searchDebounce]);

  const search = (input: string) => {
    setSearchPage(1);
    setSearchImages([]);
    setSearchInput(input);
  };

  useEffect(() => {
    if (cardProps?.imageDetails?.imageTypeSelected) {
      setActiveTab(cardProps?.imageDetails?.imageTypeSelected);
    }
  }, [cardProps?.imageDetails?.imageTypeSelected]);

  const imageEditorPropsURL = useMemo(() => {
    if (cardProps?.url || cardProps?.imageDetails?.url) {
      if (isValidImageOnActiveTab) {
        const url = cardProps?.url || cardProps?.imageDetails?.url;

        if (url?.startsWith('http')) {
          if (url.includes('unsplash')) {
            return `${url}&w=500`;
          }
          return url;
        }
        return (cardProps?.uploadedImgBlob ?? cardProps.imageBlob) as string;
      }
    }

    return null;
  }, [cardProps, isValidImageOnActiveTab]);

  const imageEditorPropsImageName = useMemo(() => {
    if (cardProps?.imageDetails?.userUploadedImageDetails?.customImageName) {
      return cardProps?.imageDetails?.userUploadedImageDetails?.customImageName;
    }
    if (cardProps?.customImageName) {
      return cardProps?.customImageName;
    }
    return 'unnamed_file';
  }, [cardProps]);

  const handleLoadImage = useCallback(
    file => {
      updateImageCardProps(file, true);
    },
    [updateImageCardProps]
  );

  const imageEditorProps = useMemo<ImageEditorProps>(
    () => ({
      ...defaultImageEditorProps,
      image: imageEditorPropsURL,
      fileName: imageEditorPropsImageName,
      editorRef,
      setEditorRef,
      uploadProgress: progress,
      handleLoadImage,
      disableEditor: true,
      hideUploadIcon: true,
      disabled: saving,
      validationFailed,
      boxSizeClass: styles.imgEditorBoxSizeWithCaption,
      avatarEditorClass: styles.imageEditorAvatarEditor,
      staticImageClass: styles.imageEditorStaticImageWithCaption,
      imgBorderHeight: 0,
      imgBorderWidth: 0,
      updateImageCardProps,
      handleRemove,
      buttons: (
        <div className={styles.fcIcons}>
          <div
            onClick={() => handleRemove()}
            className={styles.deleteIconWrapper}
          >
            <img
              className={styles.deleteIcon}
              src={deleteIcon}
              alt="delete icon"
            />
          </div>
        </div>
      ),
      text: (
        <div>
          <div>
            <span className={styles.imgEditorUnderlineText}>
              Upload an image
            </span>{' '}
            or drag it here
          </div>
          <div>
            <span className={styles.imgEditorMutedText}>
              PNGs, JPGs, or GIFs up to 10MB
            </span>
          </div>
        </div>
      ),
    }),
    [
      handleLoadImage,
      editorRef,
      handleRemove,
      updateImageCardProps,
      progress,
      validationFailed,
      saving,
      imageEditorPropsURL,
      imageEditorPropsImageName,
    ]
  );

  const onTabChange = React.useCallback(
    (key: string) => {
      setSearchPage(1);
      setSearchInput('');
      setSearchImages([]);
      setSearchType(key as typeof searchType);
      searchKey = Math.random();
      setSearching(false);
      searchDebounce.cancel();

      // reset unsplash link
      setUnsplashDownloadLink(null);
    },
    [searchDebounce]
  );

  const handleImageSelectWithoutChangeTab = React.useCallback(
    (imageDetails: Partial<FeedCardImageDetailsInput>) => {
      handleImageSelect(imageDetails);

      const form = document.getElementsByClassName('modal-form');
      if (form && form[0]) {
        dispatch(updateGraphicFormScrollPos(form[0].scrollTop));
      }
    },
    [handleImageSelect, dispatch]
  );

  useEffect(() => {
    if (saving && unsplashDownloadLink) {
      API.graphql(
        graphqlOperation(unsplashTrackDownload, {
          input: {
            downloadLocation: unsplashDownloadLink,
          },
        })
      );
    }
  }, [saving, unsplashDownloadLink]);

  const ImageEditorWrapper = React.useMemo(() => {
    return (
      <div className={styles.formWrapper}>
        <div style={{ width: 'fit-content', margin: 'auto', minHeight: 223 }}>
          <ImageEditor props={imageEditorProps} />
        </div>

        <CardFormTextArea
          name="graphicCaption"
          fieldLabel="Caption (optional)"
          defaultValue={caption}
          required={false}
          rows={1}
          showCount
          maxLength={200}
          onChange={handleInputChange(value =>
            dispatch(
              addCardProps('imageDetails', {
                ...cardProps?.imageDetails,
                caption: value,
              })
            )
          )}
        />
        <CardFormInput
          name="graphicAlt"
          fieldLabel="Alt text"
          tooltip="Alt text is a brief description of an image that only appears if the image isn't able to be displayed.
        It's particulary important for people who use screen readers, and is considered a best practice for accessibility."
          defaultValue={altDescription}
          required={false}
          onChange={handleInputChange(value =>
            dispatch(
              addCardProps('imageDetails', {
                ...cardProps?.imageDetails,
                altDescription: value,
              })
            )
          )}
        />
      </div>
    );
  }, [
    imageEditorProps,
    altDescription,
    caption,
    dispatch,
    cardProps?.imageDetails,
  ]);

  return (
    <>
      <Tabs
        centered
        // className={styles.feedCardTabs}
        onTabClick={activeKey => setActiveTab(activeKey as FeedCardImageType)}
        onChange={activeKey => onTabChange(activeKey)}
        activeKey={activeTab}
      >
        <Tabs.TabPane tab="Upload" key={FeedCardImageType.userUploadedImage}>
          {ImageEditorWrapper}
        </Tabs.TabPane>
        {isUnsplashEnabled && (
          <Tabs.TabPane tab="Image" key={FeedCardImageType.unsplashImage}>
            {cardProps?.imageDetails?.imageTypeSelected ===
            FeedCardImageType.unsplashImage ? (
              ImageEditorWrapper
            ) : (
              <div style={{ margin: '2px 4px' }}>
                <Input
                  onClick={event => event.stopPropagation}
                  placeholder="Search free high-resolution photos from Unsplash"
                  onInput={ev => search(ev.currentTarget.value)}
                  value={searchInput}
                  addonAfter={<SearchOutlined />}
                  style={{ marginBottom: '16px' }}
                />

                {searchImages.length > 0 && (
                  <>
                    <Row className={styles.graphicFormSearchRow}>
                      <Col>
                        <Masonry
                          breakpointCols={2}
                          className={styles.graphicCardMasonryGrid}
                        >
                          {(searchImages as UnsplashSearchResult[]).map(
                            image => (
                              <img
                                key={image.id}
                                src={`${image.urls?.full}&w=${imageWidth}`}
                                width={`${
                                  (image.width || 0) *
                                  calcImageRatio(image.width || 0)
                                }px`}
                                height={`${
                                  (image.height || 0) *
                                  calcImageRatio(image.width || 0)
                                }px`}
                                alt={image.alt_description || ''}
                                style={{
                                  cursor: 'pointer',
                                }}
                                onClick={() => {
                                  handleImageSelectWithoutChangeTab({
                                    url: image.urls!.full!,
                                    width: image.width,
                                    height: image.height,
                                    description: image.description,
                                    altDescription: image.alt_description,
                                    attributionFirstName:
                                      image.user?.first_name,
                                    attributionLastName: image.user?.last_name,
                                    attributionFullName: image.user?.name,
                                    attributionUserName: image.user?.username,
                                    imageTypeSelected:
                                      FeedCardImageType.unsplashImage,
                                  });
                                  setUnsplashDownloadLink(
                                    image.links?.download_location ?? null
                                  );
                                }}
                              />
                            )
                          )}
                        </Masonry>
                      </Col>
                    </Row>
                  </>
                )}
                <Row className={styles.graphicFormSearchRow}>
                  <Col>
                    {searching && (
                      <img
                        alt="Loading"
                        src={loading}
                        style={{
                          marginTop: '20px',
                          mixBlendMode: 'darken',
                          width: '50%',
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </div>
            )}
          </Tabs.TabPane>
        )}
        {isGiphyEnabled && (
          <Tabs.TabPane tab="GIF" key={FeedCardImageType.giphyImage}>
            {cardProps?.imageDetails?.imageTypeSelected ===
            FeedCardImageType.giphyImage ? (
              ImageEditorWrapper
            ) : (
              <div style={{ margin: '2px 4px' }}>
                <Input
                  onClick={event => event.stopPropagation}
                  placeholder="Search for GIFs and stickers from Giphy"
                  onInput={ev => search(ev.currentTarget.value)}
                  value={searchInput}
                  addonAfter={<SearchOutlined />}
                  style={{ marginBottom: '16px' }}
                />

                {searchImages.length > 0 && (
                  <>
                    <Row className={styles.graphicFormSearchRow}>
                      <img src={giphyLogo} alt="giphy logo" />
                      <Col>
                        <Masonry
                          breakpointCols={2}
                          className={styles.graphicCardMasonryGrid}
                        >
                          {(searchImages as GiphySearchResult[]).map(image => (
                            <img
                              key={image.id}
                              src={`${image.images?.downsized?.url}`}
                              width={`${
                                (image.images?.downsized?.width || 0) *
                                calcImageRatio(
                                  image.images?.downsized?.width || 0
                                )
                              }px`}
                              height={`${
                                (image.images?.downsized?.height || 0) *
                                calcImageRatio(
                                  image.images?.downsized?.width || 0
                                )
                              }px`}
                              alt=""
                              style={{
                                cursor: 'pointer',
                              }}
                              onClick={() =>
                                handleImageSelectWithoutChangeTab({
                                  url: image.images!.original!.url!,
                                  width: image.images?.original?.width,
                                  height: image.images?.original?.height,
                                  imageTypeSelected:
                                    FeedCardImageType.giphyImage,
                                })
                              }
                            />
                          ))}
                        </Masonry>
                      </Col>
                    </Row>
                  </>
                )}
                <Row className={styles.graphicFormSearchRow}>
                  <Col>
                    {searching && (
                      <img
                        alt="Loading"
                        src={loading}
                        style={{
                          marginTop: '20px',
                          mixBlendMode: 'darken',
                          width: '50%',
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </div>
            )}
          </Tabs.TabPane>
        )}
      </Tabs>
      <div ref={loadMoreRef} className={styles.graphicCardLoadMore} />
    </>
  );
};

export default GraphicForm;
